<template>
	<div class="flex flex_column flex_items_start">
		<div v-if="needsAdditionalDocuments" class="flex flex_column w_100">
			<ReceivableDocumentFileUpload
				v-for="(file, index) in additionalRequiredDocuments"
				:key="file"
				:file="file"
				:for-installment-contract="forInstallmentContract"
				:uploadFunction="uploadAdditionalDocument"
				@upload="(newFile) => updateAdditionalRequiredDocument(index, newFile)"
				:default-type="file.type"
				:allow-change-type="false"
				:required="true"
				:data-vv-name="`additionalRequiredDocument_${file.type}`"
				v-validate="modelValidations.additionalRequiredDocument"
				:error="errors.first(`additionalRequiredDocument_${file.type}`)"
			/>
		</div>
		<a class="margin_top pointer" @click="toggleAdditionalOptionalDocuments">
			{{ $t("seller.upload.additionalDocuments.collapseText") }}
			<i class="fa" :class="collapseAdditionalDocuments ? 'fa-caret-down' : 'fa-caret-up'"></i>
		</a>
		<div v-if="!collapseAdditionalDocuments" class="flex flex_column w_100">
			<ReceivableDocumentFileUpload
				v-for="(file, index) in additionalOptionalDocuments"
				:key="file"
				:file="file"
				:for-installment-contract="forInstallmentContract"
				:uploadFunction="uploadAdditionalDocument"
				@upload="(newFile) => updateAdditionalOptionalDocument(index, newFile)"
				@delete="() => removeAdditionalOptionalDocument(index)"
			/>
			<md-button class="md-just-icon md-just-icon-small md-primary" @click="addEmptyAdditionalOptionalDocument">
				<i class="fa fa-plus"></i>
			</md-button>
		</div>
	</div>
</template>

<script>
import ReceivableDocumentFileUpload from "@/components/templates/seller/ReceivableDocumentFileUpload.vue";
import {receivableApi} from "@/common/api/receivableApi";
import {Validator} from "vee-validate";

export default {
	components: { ReceivableDocumentFileUpload },
	props: {
		forInstallmentContract: { type: Boolean, default: false },
		validator: { type: Validator }, // Parent validator instance since the default Vue inject mechanism leads to problems with the modal this is embedded in
	},
	data() {
		return {
			additionalDocuments: [],
			requiredDocumentFileTypes: [],
			collapseAdditionalDocuments: true,
			modelValidations: {
				additionalRequiredDocument: {
					required: true,
					required_property: {
						name: "fileId"
					}
				}
			},
		};
	},
	created() {
		// Update the internal validator instance
		this.$validator = this.validator;
	},
	computed: {
		needsAdditionalDocuments() {
			return this.requiredDocumentFileTypes && this.requiredDocumentFileTypes.length > 0;
		},
		additionalRequiredDocuments() {
			if (this.needsAdditionalDocuments) {
				return this.additionalDocuments.slice(0, this.requiredDocumentFileTypes.length);
			} else {
				return [];
			}
		},
		additionalOptionalDocuments() {
			if (this.needsAdditionalDocuments) {
				return this.additionalDocuments.slice(this.requiredDocumentFileTypes.length);
			} else {
				return this.additionalDocuments;
			}
		},
	},
	methods: {
		initializeDocumentsWithRequirements(additionalDocuments, requiredDocumentFileTypes) {
			this.additionalDocuments = additionalDocuments;
			this.requiredDocumentFileTypes = requiredDocumentFileTypes;

			let unhandledDocuments = [...this.additionalDocuments];
			this.additionalDocuments.length = 0; // clear the existing array, since we are rebuilding it while keeping the original reference

			// We make sure there is a default entry per required type.
			if (this.needsAdditionalDocuments) {
				this.requiredDocumentFileTypes.forEach(type => {
					// Check if we have an already uploaded file for the required type
					let existingRequiredFileForTypeIndex = unhandledDocuments.findIndex(file => file.type === type);
					if (existingRequiredFileForTypeIndex > -1) {
						// Use the first required file matching the type
						this.addExistingAdditionalRequiredDocument(unhandledDocuments[existingRequiredFileForTypeIndex]);

						// Mark this file as already handled.
						unhandledDocuments.splice(existingRequiredFileForTypeIndex, 1);
					} else {
						// No existing file for the required type found yet, create an empty required entry for this type
						this.addEmptyAdditionalRequiredDocument(type);
					}
				});
			}

			// Any not yet handled files are optional and added at the end of the list
			if (unhandledDocuments.length > 0) {
				unhandledDocuments.forEach((file) => this.additionalDocuments.push(file));

				this.collapseAdditionalDocuments = false;
			}
		},
		toggleAdditionalOptionalDocuments() {
			this.collapseAdditionalDocuments = !this.collapseAdditionalDocuments;

			// Show an initial empty entry if expanded and there are no entries yet
			if (!this.collapseAdditionalDocuments && this.additionalOptionalDocuments.length === 0) {
				this.addEmptyAdditionalOptionalDocument();
			}
		},
		uploadAdditionalDocument(type, params) {
			return receivableApi.uploadAdditionalDocument(type, {file: params.file });
		},
		addEmptyAdditionalOptionalDocument() {
			this.additionalDocuments.push({
				type: null,
				fileId: null,
				fileName: null
			});
		},
		removeAdditionalOptionalDocument(optionalListIndex) {
			let adjustedIndex = this.getAdditionalDocumentsListIndexFromOptionalListIndex(optionalListIndex);
			this.additionalDocuments.splice(adjustedIndex, 1);
		},
		updateAdditionalOptionalDocument(optionalListIndex, file) {
			let adjustedIndex = this.getAdditionalDocumentsListIndexFromOptionalListIndex(optionalListIndex);
			this.additionalDocuments[adjustedIndex] = file;
		},
		addExistingAdditionalRequiredDocument(file) {
			this.additionalDocuments.push(file);
		},
		addEmptyAdditionalRequiredDocument(type) {
			this.additionalDocuments.push({
				type: type,
				fileId: null,
				fileName: null
			});
		},
		updateAdditionalRequiredDocument(index, file) {
			this.additionalDocuments[index] = file;
		},
		getAdditionalDocumentsListIndexFromOptionalListIndex(optionalListIndex) {
			return this.requiredDocumentFileTypes.length + optionalListIndex;
		}
	}
};
</script>